import { LogicalAndFilter } from '@cubejs-client/core';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import { HCP_HCO_CUBE_NAME } from '@/api/hco/constants';
import { HCP_CUBE_NAME } from '@/api/hcp/interaction/types';
import { buildResolverScopeFilters } from '@/api/hco/filters';
import { HCOKeys } from '@/api/queries/hco/cacheKeys';
import useQuery from '@/api/queries/useQuery';
import { FilterLimitPresets } from '@/models/filter/filterDefaultLimits';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { toNumberOrNull, toStringOrNull } from '@/utils/cube/mapper';
import { useAllowlistedGlobalFilters } from '@/utils/cube/useGetGlobalFilters';

const RESOLVER_LIMIT = FilterLimitPresets.LIMIT_50K;

const HCP_BK_DIMENSION = `${HCP_HCO_CUBE_NAME}.hcp_bk`;
const HCP_NAME_DIMENSION = `${HCP_CUBE_NAME}.hcp_full_name_backward`;

const EMPTY_HCP_BKS: readonly string[] = [];
const EMPTY_HCP_NAMES_BY_BK: Readonly<Record<string, string>> = {};

interface ResolverResponse {
  totalCount?: number;
  data?: CubeRawRow[];
}

interface ResolverResult {
  hcpBks: readonly string[];
  hcpNamesByBk: Readonly<Record<string, string>>;
  totalCount: number;
}

const EMPTY_RESOLVER_RESULT: ResolverResult = {
  hcpBks: EMPTY_HCP_BKS,
  hcpNamesByBk: EMPTY_HCP_NAMES_BY_BK,
  totalCount: 0,
};

export interface HcoFilteredHcpBks {
  hcpBks: readonly string[];
  hcpNamesByBk: Readonly<Record<string, string>>;
  isResolving: boolean;
  isError: boolean;
  isActive: boolean;
  isTruncated: boolean;
  scopeVersion: number;
}

const countActivePredicates = (filters: LogicalAndFilter): number => filters.and.length;

const selectResolverResult = ({ totalCount, data }: ResolverResponse): ResolverResult => {
  if (!data) return EMPTY_RESOLVER_RESULT;
  const seen = new Set<string>();
  const hcpNamesByBk: Record<string, string> = {};
  data.forEach(hcpRow => {
    const hcpBk = toStringOrNull(hcpRow[HCP_BK_DIMENSION]);
    const hcpName = toStringOrNull(hcpRow[HCP_NAME_DIMENSION]);
    if (hcpBk) seen.add(hcpBk);
    if (hcpBk && hcpName) hcpNamesByBk[hcpBk] = hcpName;
  });
  return { hcpBks: Array.from(seen), hcpNamesByBk, totalCount: toNumberOrNull(totalCount) ?? seen.size };
};

// Rooted on hcp_hco (not hcp_in_hco) because it is the only HCO cube that joins to every filterable HCP/HCO attribute cube; rooting on hcp_in_hco is why Territory/Area-of-Interest/Other-Specialty/Segmentation filters returned zero rows.
export const useHcoFilteredHcpBks = (
  hcoBk: string | undefined,
  includeAllDepartments: boolean,
  alwaysResolve: boolean = false,
): HcoFilteredHcpBks => {
  const globalFilters = useAllowlistedGlobalFilters({
    includeHco: includeAllDepartments,
    excludeDisabled: false,
  });

  const hasActiveFilters = countActivePredicates(globalFilters) > 0;
  const isActive = (alwaysResolve || hasActiveFilters) && !!hcoBk;

  const filters = useMemo(
    () => [globalFilters, ...buildResolverScopeFilters(hcoBk ?? '', includeAllDepartments)],
    [globalFilters, hcoBk, includeAllDepartments],
  );

  const { data, dataUpdatedAt, isLoading, isError } = useQuery<ResolverResponse, AxiosError, ResolverResult>({
    payload: {
      dimensions: [HCP_BK_DIMENSION, HCP_NAME_DIMENSION],
      filters,
      limit: RESOLVER_LIMIT,
      total: true,
    },
    options: {
      queryKey: HCOKeys.filteredHcpBks(hcoBk, includeAllDepartments, globalFilters),
      select: selectResolverResult,
      enabled: isActive,
    },
  });

  const isTruncated = isActive && (data?.totalCount ?? 0) > RESOLVER_LIMIT;
  const hcpBks = data?.hcpBks ?? EMPTY_HCP_BKS;
  const hcpNamesByBk = data?.hcpNamesByBk ?? EMPTY_HCP_NAMES_BY_BK;
  const isResolving = isActive && isLoading;

  return useMemo(
    () => ({
      hcpBks,
      hcpNamesByBk,
      isResolving,
      isError,
      isActive,
      isTruncated,
      scopeVersion: dataUpdatedAt,
    }),
    [dataUpdatedAt, hcpBks, hcpNamesByBk, isActive, isError, isResolving, isTruncated],
  );
};
